import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useThemeColors } from '../context/ThemeContext';
import { dsv2, dsv2Light } from '../styles/designSystemV2';
import { useTranslation } from 'react-i18next';
import { X, Info, AlertTriangle, Wrench } from 'lucide-react';
import { snapshotMigrationConfig } from '../utils/migrationConfig';
import { fetchActiveAnnouncements } from '../api/audit';

export default function GlobalAnnouncement() {
  const { isDark } = useThemeColors();
  const tokens = isDark ? dsv2 : dsv2Light;
  const { t } = useTranslation();
  const [ann, setAnn] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let vivo = true;
    let unsub = null;

    const mostrar = (latest) => {
      if (!vivo) return;
      setAnn(latest);
      setDismissed(latest ? localStorage.getItem('annDismissed') === latest.id : false);
    };

    (async () => {
      let porApi = false;
      try {
        porApi = (await snapshotMigrationConfig()).readsFrom('audit') === 'postgres';
      } catch (_) { /* ante cualquier duda, el camino viejo */ }

      if (porApi) {
        try {
          // Deja de ser una suscripción, y eso es una mejora, no una pérdida:
          // hoy mantiene un `onSnapshot` abierto en TODAS las páginas para TODOS
          // los usuarios, para pintar un aviso que se publica cada varias
          // semanas. El API ya devuelve solo los activos, no borrados y dentro
          // de su ventana, ordenados por fecha.
          const r = await fetchActiveAnnouncements({ page: 1, pageSize: 1 });
          mostrar(r.items[0] || null);
        } catch (_) {
          // Un aviso que no se pudo leer no puede tumbar la app entera: es un
          // banner, y va montado en todas las pantallas.
        }
        return;
      }

      unsub = onSnapshot(
        query(collection(db, 'announcements'), where('active', '==', true)),
        (snap) => {
          const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          list.sort((a, b) => (b.createdAt?.toDate?.()?.getTime?.() || 0) - (a.createdAt?.toDate?.()?.getTime?.() || 0));
          mostrar(list[0] || null);
        },
        () => {}
      );
    })();

    return () => { vivo = false; if (unsub) unsub(); };
  }, []);

  if (!ann || dismissed) return null;

  const colorMap = { info: tokens.blue, warning: tokens.amber, maintenance: tokens.red };
  const color = colorMap[ann.type] || tokens.blue;
  const Icon = ann.type === 'maintenance' ? Wrench : ann.type === 'warning' ? AlertTriangle : Info;
  const dismiss = () => { try { localStorage.setItem('annDismissed', ann.id); } catch (e) { /* ignore */ } setDismissed(true); };

  return (
    <div style={{ background: color, color: '#fff', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, lineHeight: 1.4 }}>
      <Icon size={18} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1 }}><strong>{ann.title}</strong>{ann.body ? ` — ${ann.body}` : ''}</div>
      <button onClick={dismiss} title={t('admin.annDismiss')} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', display: 'inline-flex', flexShrink: 0 }}>
        <X size={18} />
      </button>
    </div>
  );
}
